import React, { FC, useEffect, useMemo } from 'react';
import { Dialog } from '@mui/material';
import { useTranslation } from '@fdha/common-hooks';
import {
  DishInformationV2,
  EmptyStateCard,
  LoadingStateV2,
  SectionType,
} from '@fdha/web-ui-library';
import { MealOptionType } from '@fdha/graphql-api-patient';
import { useDishDetails } from '@fdha/common-hooks';
import type {
  DishData,
  MealKitData,
} from '@fdha/web-ui-library/src/components/DishInformationV2/types';

interface DishDetailsProps {
  selectedCount: number;
  requiredCount: number;
  onClose: () => void;
  isPreviewMode?: boolean;
  open?: boolean;
  dishItem: {
    dishId: string;
    mealOptionType: MealOptionType;
    menuOptionId: string;
    selectedVariantId?: string;
    sectionName: SectionType;
  };
}

const DishDetails: FC<DishDetailsProps> = ({
  dishItem,
  selectedCount,
  requiredCount,
  onClose,
  isPreviewMode = false,
  open = true,
}) => {
  const { translate } = useTranslation();
  const {
    isLoading,
    dishData,
    mealKitData,
    variantError,
    selectedVariant,
    quantity,
    setDishDetails,
    resetDishDetails,
    onVariantChange,
    validateAndAdd,
    setQuantity,
  } = useDishDetails();

  // Set up dish details when component mounts
  useEffect(() => {
    setDishDetails({
      visible: true,
      selectedDish: {
        dishId: dishItem.dishId,
        mealOptionType: dishItem.mealOptionType,
        sectionName: dishItem.sectionName,
        variantId: dishItem.selectedVariantId,
        menuOptionId: dishItem.menuOptionId,
        quantity: 0,
      },
    });

    return resetDishDetails;
  }, [
    dishItem.dishId,
    dishItem.mealOptionType,
    dishItem.menuOptionId,
    dishItem.sectionName,
    dishItem.selectedVariantId,
    resetDishDetails,
    setDishDetails,
  ]);

  // Transform GraphQL data to DishInformationV2 format
  const transformedData: DishData | MealKitData | null = useMemo(() => {
    if (dishItem.mealOptionType === MealOptionType.Dish && dishData?.dish) {
      const dish = dishData.dish;
      return {
        id: dish.id,
        name: dish.name,
        photoUrl: dish.photoUrl || '',
        description: dish.description || '',
        ingredients: dish.ingredients || '',
        tags: dish.tags || [],
        nutritionalValuesUrl: dish.nutritionalValuesUrl || '',
        instructions: dish.instructions || [],
      } as DishData;
    }

    if (
      dishItem.mealOptionType === MealOptionType.MealKit &&
      mealKitData?.mealKit
    ) {
      const mealKit = mealKitData.mealKit;
      return {
        id: mealKit.id,
        name: mealKit.name,
        photoUrl: mealKit.photoUrl || '',
        description: mealKit.description || '',
        ingredients: mealKit.ingredients || [],
        tags: mealKit.tags || [],
        variants: mealKit.variants || [],
        nutritionalValues: mealKit.nutritionalValues || [],
        equipment: mealKit.equipment || [],
        instructions: mealKit.instructions || [],
      } as unknown as MealKitData;
    }

    return null;
  }, [dishItem.mealOptionType, dishData, mealKitData]);

  // Handle add to cart using the hook's validateAndAdd method
  const handleAddToCart = () => {
    if (!isPreviewMode) {
      // Use the hook's validateAndAdd method
      const success = validateAndAdd();

      if (success) {
        // Close the dialog after successful update
        onClose();
      }
    }
  };

  const renderLoadingState = () => (
    <LoadingStateV2 isPreviewMode={isPreviewMode} />
  );

  const renderContent = () => {
    if (isLoading) {
      return renderLoadingState();
    }

    if (!transformedData) {
      return (
        <EmptyStateCard
          message={translate(
            'meals:dishDetails.emptyState',
            "This item doesn't exist."
          )}
          i18nKeyMessage="meals:dishDetails.emptyState"
        />
      );
    }

    const isEntree = dishItem.sectionName === 'entreeDishes';

    return (
      <DishInformationV2
        data={transformedData}
        mealProvisionType={dishItem.mealOptionType}
        selectedCount={selectedCount}
        requiredCount={requiredCount}
        maxQuantity={Math.max(requiredCount - selectedCount, 0)}
        onAddToCart={handleAddToCart}
        onClose={onClose}
        isPreviewMode={isPreviewMode}
        currentMealKitVariantId={selectedVariant}
        isEntree={isEntree}
        onVariantChange={onVariantChange}
        showVariantError={variantError}
        quantity={quantity}
        onQuantityChange={setQuantity}
        v2={true}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          display: 'flex',
          width: '1200px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '1px',
        },
      }}
    >
      {renderContent()}
    </Dialog>
  );
};

export default DishDetails;
